import React, { useEffect, useMemo, useState } from 'react';
import {
  ShoppingCart, Plus, Loader2, CheckCircle2, PackageX, X, Pencil, Trash2,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Dialog, cn } from '../../ui';
import { getIcon } from '../icons';
import { useTiposCrud } from '../hooks/useTiposCrud';
import { useCompras } from '../hooks/useCompras';
import ScopeTabs from '../components/ScopeTabs';
import { normAmbito, etiquetaAmbito } from '../utils/rutaTipo';
import GuiaRapida from '../components/GuiaRapida';
import LocalidadSelect from '../components/LocalidadSelect';
import { camposDeUbicacion } from '../utils/ubicacion';

/**
 * Página: Compras (stock entries) — sub-bloque III.9
 *
 * NUEVA pantalla (no existía en III.1; este bloque añade su ruta y su
 * entrada en el SubSidebar).
 *
 *   - Listado de compras registradas
 *   - Form "Nueva compra": tipo (solo has_stock) → empresa según scope →
 *     cantidad, fecha, proveedor, coste, factura, notas → campos comunes
 *     del schema + tabla de campos por unidad (per_unit) → preview EN
 *     VIVO del rango de referencias que se generarán
 *
 * Backend ya implementado en II.4.5 (POST /stock-entries crea N activos
 * + N eventos 'compra' atómicamente). Mock mientras no haya BBDD.
 */

function rangoPreview(tipo, cantidad) {
  if (!tipo || !tipo.ref_prefix || !cantidad || cantidad < 1) return null;
  const start = (tipo.ref_counter || 0) + 1;
  const pad = tipo.ref_padding || 4;
  const first = `${tipo.ref_prefix}-${String(start).padStart(pad, '0')}`;
  const last = `${tipo.ref_prefix}-${String(start + cantidad - 1).padStart(pad, '0')}`;
  return cantidad === 1 ? first : `${first} … ${last}`;
}

export default function Compras() {
  const { tipos, loading: tiposLoading } = useTiposCrud();
  const {
    compras, loading, creating, empresasDe,
    crearCompra, editarCompra, eliminarCompra, isMock,
  } = useCompras();

  const [showForm, setShowForm] = useState(false);
  const [okMsg, setOkMsg] = useState(null);
  // Ámbito ACTIVO de la pantalla. Gobierna el listado Y el formulario.
  // Antes se llamaba `listScope` y solo filtraba el listado: el selector de
  // tipos ofrecía los dos ámbitos (useTiposCrud sin argumento) y el
  // formulario deducía el suyo del tipo elegido. No grababa nada mal —el
  // backend deriva el ámbito del asset_type_id, no de la UI—, pero se podía
  // registrar una compra interna con la pestaña en COLABORADORES y la fila
  // desaparecía del listado, filtrado al otro ámbito.
  const [scopeActivo, setScopeActivo] = useState('interno');

  // Modales editar/eliminar (III.9.b). `editing` y `deleting` guardan
  // la fila completa de la compra que se está modificando.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [rowBusy, setRowBusy] = useState(false);
  const [rowErr, setRowErr] = useState(null);

  // Listado filtrado por ámbito (GRUPOLAE / Colaboradores)
  const comprasFiltradas = useMemo(
    () => compras.filter((c) => c.scope === scopeActivo),
    [compras, scopeActivo]
  );

  // Tipos ofrecibles en el formulario: con stock Y del ámbito activo. Se
  // filtra en cliente en vez de pedirle el ámbito al hook (como sí hace
  // TiposList) para no repetir la petición en cada cambio de pestaña: el
  // catálogo son unas decenas de filas y ya está cargado.
  const tiposStock = useMemo(
    () => tipos.filter((t) => t.has_stock && normAmbito(t.scope) === scopeActivo),
    [tipos, scopeActivo]
  );

  // ── Estado del formulario ──
  const [tipoId, setTipoId] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [proveedor, setProveedor] = useState('');
  const [costeUnitario, setCosteUnitario] = useState('');
  const [numFactura, setNumFactura] = useState('');
  const [notas, setNotas] = useState('');
  const [common, setCommon] = useState({});
  const [perUnit, setPerUnit] = useState([]);
  const [formErr, setFormErr] = useState(null);

  const tipo = useMemo(
    () => tiposStock.find((t) => String(t.id) === String(tipoId)) || null,
    [tiposStock, tipoId]
  );
  // Con el selector ya acotado, el ámbito del formulario es el de la
  // pestaña. Se sigue leyendo del tipo cuando hay uno elegido (es la
  // fuente que manda para el payload y para el backend), y cae a la
  // pestaña mientras no hay tipo, para que las etiquetas del formulario
  // vacío no digan "Empresa" estando en COLABORADORES.
  const scope = tipo ? normAmbito(tipo.scope) : scopeActivo;
  const empresas = empresasDe(scope);
  const schema = tipo?.fields_schema || [];

  // Bug 6: los campos *_link NO se piden en el formulario de compra.
  // - company_link → se propaga automáticamente desde la cabecera (la
  //   empresa elegida arriba va a todos los activos creados).
  // - user_link    → al comprar stock fresco no se asigna a nadie; se
  //   rellena después con un evento de asignación.
  // - office_link / business_link → idem, se editan en el activo o se
  //   asignan por otro flujo.
  // Filtramos aquí para que el usuario no tenga que rellenarlos a mano
  // y, sobre todo, no propague valores incorrectos o vacíos.
  const LINK_TYPES = ['user_link', 'office_link', 'company_link', 'business_link'];
  const editableSchema = schema.filter(
    (f) => !LINK_TYPES.includes(f && f.type)
  );
  const commonFields = editableSchema.filter((f) => !f.per_unit);
  const ubic = camposDeUbicacion(schema);
  const perUnitFields = editableSchema.filter((f) => f.per_unit);

  // Reset de campos dependientes al cambiar de tipo
  useEffect(() => {
    setEmpresaId('');
    setCommon({});
  }, [tipoId]);

  // Mantener la tabla per-unit sincronizada con la cantidad
  useEffect(() => {
    const n = Math.max(0, Number(cantidad) || 0);
    setPerUnit((prev) => {
      const next = [...prev];
      next.length = n;
      for (let i = 0; i < n; i++) if (!next[i]) next[i] = {};
      return next;
    });
  }, [cantidad, tipoId]);

  const preview = rangoPreview(tipo, Number(cantidad));

  function resetForm() {
    setTipoId('');
    setEmpresaId('');
    setCantidad(1);
    setFecha(new Date().toISOString().slice(0, 10));
    setProveedor('');
    setCosteUnitario('');
    setNumFactura('');
    setNotas('');
    setCommon({});
    setPerUnit([]);
    setFormErr(null);
  }

  async function handleSubmit() {
    setFormErr(null);
    if (!tipo) return setFormErr('Selecciona un tipo');
    if (!empresaId) return setFormErr(scope === 'colab' ? 'Selecciona una colaboradora' : 'Selecciona una empresa');
    if (!cantidad || Number(cantidad) < 1) return setFormErr('La cantidad debe ser ≥ 1');
    if (!fecha) return setFormErr('La fecha de compra es obligatoria');

    const payload = {
      asset_type_id: tipo.id,
      cantidad: Number(cantidad),
      fecha_compra: fecha,
      proveedor: proveedor || null,
      coste_unitario: costeUnitario === '' ? null : Number(costeUnitario),
      numero_factura: numFactura || null,
      notas: notas || null,
      common_fields: common,
      per_unit_fields: perUnit,
    };
    if (scope === 'colab') payload.empresa_colaboradora_id = Number(empresaId);
    else payload.empresa_id = Number(empresaId);

    try {
      const res = await crearCompra(payload, tipo);
      const refs = res?.activos?.references || [];
      setOkMsg(
        refs.length === 1
          ? `Compra registrada · se generó ${refs[0]}`
          : `Compra registrada · ${refs.length} unidades: ${refs[0]} … ${refs[refs.length - 1]}`
      );
      setShowForm(false);
      resetForm();
      setTimeout(() => setOkMsg(null), 6000);
    } catch (e) {
      setFormErr(e?.response?.data?.error || e.message || 'No se pudo registrar la compra');
    }
  }

  // ── Handlers Editar/Eliminar (III.9.b) ──

  async function confirmEdit() {
    setRowErr(null);
    if (!editing) return;
    setRowBusy(true);
    try {
      // Construir payload con SOLO los campos que han cambiado vs original.
      const original = compras.find((c) => c.id === editing.id);
      if (!original) throw new Error('Compra no encontrada en el listado');
      const payload = {};
      const fields = [
        'fecha_compra', 'proveedor', 'numero_factura', 'notas',
        'coste_unitario', 'coste_total',
      ];
      for (const f of fields) {
        const v = editing[f];
        const o = original[f];
        const norm = (x) => (x === '' || x == null ? null : x);
        // Para decimales convertimos a Number para comparar bien
        const nv = (f === 'coste_unitario' || f === 'coste_total')
          ? (norm(v) == null ? null : Number(v))
          : norm(v);
        const ov = (f === 'coste_unitario' || f === 'coste_total')
          ? (o == null ? null : Number(o))
          : norm(o);
        if (nv !== ov) payload[f] = nv;
      }
      // Empresa: distinguir el campo según el scope original.
      const empCampo = original.scope === 'colab' ? 'empresa_colaboradora_id' : 'empresa_id';
      const nEmp = editing[empCampo];
      const oEmp = original[empCampo];
      if (Number(nEmp || 0) !== Number(oEmp || 0)) {
        payload[empCampo] = nEmp ? Number(nEmp) : null;
      }
      if (Object.keys(payload).length === 0) {
        // Nada que cambiar — cerramos sin tocar.
        setEditing(null);
        return;
      }
      if (editing.edit_reason) payload.edit_reason = editing.edit_reason;
      await editarCompra(editing.id, payload);
      setEditing(null);
    } catch (e) {
      setRowErr(e?.response?.data?.error || e.message || 'No se pudo guardar');
    } finally {
      setRowBusy(false);
    }
  }

  async function confirmDelete() {
    setRowErr(null);
    if (!deleting?.delete_reason?.trim()) {
      setRowErr('El motivo de la eliminación es obligatorio');
      return;
    }
    setRowBusy(true);
    try {
      await eliminarCompra(deleting.id, deleting.delete_reason.trim());
      setDeleting(null);
    } catch (e) {
      setRowErr(e?.response?.data?.error || e.message || 'No se pudo eliminar');
    } finally {
      setRowBusy(false);
    }
  }

  return (
    <div className="px-6 py-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[hsl(var(--brand-light))]" />
            Compras
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Registro de compras · cada compra genera las unidades con su referencia
            {isMock && (
              <span className="ml-2 text-[11px] text-amber-600 font-medium">
                · datos de demostración (backend pendiente)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GuiaRapida seccion="compras" />
          {!showForm && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Nueva compra
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <ScopeTabs
          value={scopeActivo}
          onChange={(s) => {
            setScopeActivo(s);
            // Si el tipo elegido no es de la pestaña nueva, se limpia: un
            // formulario con la pestaña en COLABORADORES y un tipo interno
            // seleccionado es cómo se registra una compra que luego no
            // aparece en el listado que se está mirando. El efecto de
            // [tipoId] se encarga de empresa y campos comunes.
            if (tipo && normAmbito(tipo.scope) !== normAmbito(s)) setTipoId('');
          }}
        />
      </div>

      {okMsg && (
        <div className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4" />
          {okMsg}
        </div>
      )}

      {/* ── FORM NUEVA COMPRA ── */}
      {showForm && (
        <Card className="mb-3">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Nueva compra</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Al guardar se generan las unidades con referencia correlativa y un evento de compra por unidad
              </p>
            </div>
            <button
              onClick={() => { setShowForm(false); resetForm(); }}
              className="text-slate-400 hover:text-slate-700 p-1"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <CardContent className="p-4">
            {tiposLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando tipos…
              </div>
            ) : tiposStock.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">
                No hay tipos gestionados con stock en{' '}
                {etiquetaAmbito(scopeActivo)}. Crea uno con "stock" activado en
                Tipos, o cambia de ámbito con las pestañas de arriba.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="text-slate-500 mb-1 block">Tipo</label>
                    <select
                      value={tipoId}
                      onChange={(e) => setTipoId(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                    >
                      <option value="">Selecciona…</option>
                      {/* Sin sufijo de ámbito: ahora todas las opciones son
                          del ámbito de la pestaña, y repetirlo en cada línea
                          era ruido. */}
                      {tiposStock.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">
                      {scope === 'colab' ? 'Empresa colaboradora' : 'Empresa'}
                    </label>
                    <select
                      value={empresaId}
                      onChange={(e) => setEmpresaId(e.target.value)}
                      disabled={!tipo}
                      className={cn(
                        'w-full border border-slate-200 rounded-md px-3 py-2 bg-white',
                        !tipo && 'bg-slate-50 cursor-not-allowed'
                      )}
                    >
                      <option value="">Selecciona…</option>
                      {empresas.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">Cantidad</label>
                    <input
                      type="number"
                      min={1}
                      value={cantidad}
                      onChange={(e) => setCantidad(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">Fecha de compra</label>
                    <input
                      type="date"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                    />
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">Proveedor</label>
                    <input
                      value={proveedor}
                      onChange={(e) => setProveedor(e.target.value)}
                      placeholder="Opcional"
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">Coste unitario (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={costeUnitario}
                      onChange={(e) => setCosteUnitario(e.target.value)}
                      placeholder="Opcional"
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="text-slate-500 mb-1 block">Nº factura</label>
                    <input
                      value={numFactura}
                      onChange={(e) => setNumFactura(e.target.value)}
                      placeholder="Opcional"
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-slate-500 mb-1 block">Notas</label>
                    <input
                      value={notas}
                      onChange={(e) => setNotas(e.target.value)}
                      placeholder="Opcional"
                      className="w-full border border-slate-200 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* Preview de referencias */}
                {preview && (
                  <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs text-emerald-900">
                    Se generarán <span className="font-semibold">{cantidad}</span>{' '}
                    {Number(cantidad) === 1 ? 'unidad' : 'unidades'}:{' '}
                    <span className="font-mono font-semibold">{preview}</span>
                  </div>
                )}

                {/* Campos comunes del schema */}
                {tipo && commonFields.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Datos comunes a todas las unidades
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      {commonFields.map((f) => (
                        <div key={f.name}>
                          <label className="text-slate-500 mb-1 block">
                            {f.label || f.name}
                            {f.required && <span className="text-rose-500"> *</span>}
                          </label>
                          {ubic && f.name === ubic.localidad.name ? (
                            // Localidad del catálogo del INE de la provincia elegida (Fase 3b).
                            <LocalidadSelect
                              provincia={common[ubic.provincia.name] || ''}
                              value={common[f.name] ?? ''}
                              onChange={(v) => setCommon((c) => ({ ...c, [f.name]: v }))}
                            />
                          ) : f.type === 'select' ? (
                            <select
                              value={common[f.name] ?? ''}
                              onChange={(e) =>
                                setCommon((c) => ({
                                  ...c,
                                  [f.name]: e.target.value,
                                  // Otra provincia: la localidad de la anterior ya no vale.
                                  ...(ubic && f.name === ubic.provincia.name && e.target.value !== c[f.name]
                                    ? { [ubic.localidad.name]: '' } : {}),
                                }))
                              }
                              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
                            >
                              <option value="">—</option>
                              {(f.options || []).map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              value={common[f.name] ?? ''}
                              onChange={(e) =>
                                setCommon((c) => ({ ...c, [f.name]: e.target.value }))
                              }
                              type={f.type === 'number' || f.type === 'decimal' ? 'number' : 'text'}
                              className="w-full border border-slate-200 rounded-md px-3 py-2"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Campos por unidad */}
                {tipo && perUnitFields.length > 0 && Number(cantidad) > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Datos por unidad
                      <span className="ml-1 text-slate-400 normal-case font-normal">
                        (puedes dejarlos vacíos y rellenarlos al asignar)
                      </span>
                    </p>
                    <div className="border border-slate-200 rounded-md overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium w-28">Referencia</th>
                            {perUnitFields.map((f) => (
                              <th key={f.name} className="px-3 py-2 text-left font-medium">
                                {f.label || f.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {Array.from({ length: Number(cantidad) }).map((_, i) => {
                            const start = (tipo.ref_counter || 0) + 1;
                            const pad = tipo.ref_padding || 4;
                            const ref = `${tipo.ref_prefix}-${String(start + i).padStart(pad, '0')}`;
                            return (
                              <tr key={i}>
                                <td className="px-3 py-2 font-mono font-semibold text-slate-700">
                                  {ref}
                                </td>
                                {perUnitFields.map((f) => (
                                  <td key={f.name} className="px-3 py-2">
                                    <input
                                      value={perUnit[i]?.[f.name] ?? ''}
                                      onChange={(e) =>
                                        setPerUnit((pu) => {
                                          const next = [...pu];
                                          next[i] = { ...(next[i] || {}), [f.name]: e.target.value };
                                          return next;
                                        })
                                      }
                                      placeholder="dejar vacío y rellenar al asignar"
                                      className="w-full border border-slate-200 rounded px-2 py-1"
                                    />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {formErr && (
                  <div className="mt-3 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-700">
                    {formErr}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    disabled={creating}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleSubmit} disabled={creating}>
                    {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Registrar compra
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── LISTADO DE COMPRAS ── */}
      <Card>
        {loading ? (
          <CardContent className="p-4 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando compras…
          </CardContent>
        ) : comprasFiltradas.length === 0 ? (
          <CardContent className="p-8 text-center">
            <PackageX className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No hay compras registradas en{' '}
              {etiquetaAmbito(scopeActivo)} todavía.
            </p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-100 text-left">
                  <th className="px-4 py-2.5 font-medium">Fecha</th>
                  <th className="px-4 py-2.5 font-medium">Tipo</th>
                  <th className="px-4 py-2.5 font-medium">Empresa</th>
                  <th className="px-4 py-2.5 font-medium">Uds.</th>
                  <th className="px-4 py-2.5 font-medium">Referencias</th>
                  <th className="px-4 py-2.5 font-medium">Proveedor</th>
                  <th className="px-4 py-2.5 font-medium text-right">Coste total</th>
                  <th className="px-4 py-2.5 font-medium">Factura</th>
                  <th className="px-4 py-2.5 font-medium w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comprasFiltradas.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700 tabular-nums whitespace-nowrap">
                      {c.fecha_compra}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-slate-700">
                        {c.asset_type_nombre}
                        <Badge variant={c.scope === 'colab' ? 'purple' : 'secondary'}>
                          {c.scope === 'colab' ? 'Colab' : 'Interno'}
                        </Badge>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.empresa_nombre}</td>
                    <td className="px-4 py-3 tabular-nums">{c.cantidad}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">
                      {c.referencias?.length
                        ? c.referencias.length === 1
                          ? c.referencias[0]
                          : `${c.referencias[0]} … ${c.referencias[c.referencias.length - 1]}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.proveedor || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {c.coste_total != null
                        ? `${Number(c.coste_total).toLocaleString('es-ES')} €`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{c.numero_factura || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            setRowErr(null);
                            setEditing({
                              id: c.id,
                              fecha_compra: c.fecha_compra || '',
                              proveedor: c.proveedor || '',
                              numero_factura: c.numero_factura || '',
                              notas: c.notas || '',
                              coste_unitario: c.coste_unitario ?? '',
                              coste_total: c.coste_total ?? '',
                              empresa_id: c.empresa_id ?? '',
                              empresa_colaboradora_id: c.empresa_colaboradora_id ?? '',
                              edit_reason: '',
                            });
                          }}
                          title="Editar compra"
                          aria-label="Editar compra"
                          className="h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRowErr(null);
                            setDeleting({ ...c, delete_reason: '' });
                          }}
                          title="Eliminar compra"
                          aria-label="Eliminar compra"
                          className="h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal: Editar compra */}
      <Dialog
        open={!!editing}
        onClose={() => !rowBusy && setEditing(null)}
        title="Editar compra"
        description={
          editing
            ? `${editing.fecha_compra || ''} · solo metadata (cantidad y tipo no son editables)`
            : ''
        }
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => !rowBusy && setEditing(null)} disabled={rowBusy}>
              Cancelar
            </Button>
            <Button onClick={confirmEdit} disabled={rowBusy}>
              {rowBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Guardar
            </Button>
          </>
        }
      >
        {editing && (() => {
          const original = compras.find((c) => c.id === editing.id);
          const isColab = original?.scope === 'colab';
          const empCampo = isColab ? 'empresa_colaboradora_id' : 'empresa_id';
          const empresasList = empresasDe(original?.scope || 'interno');
          return (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={editing.fecha_compra}
                    onChange={(e) => setEditing((s) => ({ ...s, fecha_compra: e.target.value }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    {isColab ? 'Colaboradora' : 'Empresa'}
                  </label>
                  <select
                    value={editing[empCampo] || ''}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, [empCampo]: e.target.value }))
                    }
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">—</option>
                    {empresasList.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Proveedor
                </label>
                <input
                  value={editing.proveedor}
                  onChange={(e) => setEditing((s) => ({ ...s, proveedor: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    Coste unitario (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editing.coste_unitario}
                    onChange={(e) => setEditing((s) => ({
                      ...s,
                      coste_unitario: e.target.value,
                      // si el usuario edita el unitario, dejamos que el backend
                      // recalcule el total: lo borramos del payload
                      coste_total: '',
                    }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                    Coste total (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editing.coste_total}
                    onChange={(e) => setEditing((s) => ({
                      ...s,
                      coste_total: e.target.value,
                      coste_unitario: '',
                    }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Número de factura
                </label>
                <input
                  value={editing.numero_factura}
                  onChange={(e) => setEditing((s) => ({ ...s, numero_factura: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Notas
                </label>
                <textarea
                  value={editing.notas}
                  onChange={(e) => setEditing((s) => ({ ...s, notas: e.target.value }))}
                  rows={2}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                  Motivo de la edición (opcional)
                </label>
                <input
                  value={editing.edit_reason}
                  onChange={(e) => setEditing((s) => ({ ...s, edit_reason: e.target.value }))}
                  placeholder="Ej: corrección de proveedor"
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <p className="text-[11px] text-slate-400">
                Los cambios de fecha, proveedor, precio y notas se propagan
                a los {original?.cantidad} eventos `compra` asociados. La
                cantidad, el tipo y los datos por unidad NO son editables.
              </p>

              {rowErr && (
                <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
                  {rowErr}
                </p>
              )}
            </div>
          );
        })()}
      </Dialog>

      {/* Modal: Eliminar compra */}
      <Dialog
        open={!!deleting}
        onClose={() => !rowBusy && setDeleting(null)}
        title="Eliminar compra"
        description={
          deleting
            ? `${deleting.asset_type_nombre} · ${deleting.cantidad} ud · ${deleting.fecha_compra}`
            : ''
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => !rowBusy && setDeleting(null)} disabled={rowBusy}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={rowBusy}>
              {rowBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Eliminar
            </Button>
          </>
        }
      >
        {deleting && (
          <div className="space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              Esto elimina la compra y los <span className="font-semibold">{deleting.cantidad} eventos `compra`</span> asociados.
              {' '}<span className="font-semibold">Los {deleting.cantidad} activos creados</span> (
              {deleting.referencias && deleting.referencias.length > 0
                ? (deleting.referencias.length === 1
                    ? deleting.referencias[0]
                    : `${deleting.referencias[0]} … ${deleting.referencias[deleting.referencias.length - 1]}`)
                : '—'}
              ) <span className="font-semibold">siguen vivos en el inventario</span>.
              Para retirarlos hay que darlos de baja uno por uno desde su pantalla.
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5 block">
                Motivo de la eliminación <span className="text-rose-500 normal-case">*</span>
              </label>
              <textarea
                value={deleting.delete_reason}
                onChange={(e) => setDeleting((s) => ({ ...s, delete_reason: e.target.value }))}
                rows={2}
                placeholder="Ej: factura duplicada / datos erróneos"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              />
            </div>
            {rowErr && (
              <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
                {rowErr}
              </p>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
